// src/views/operateur/VeilleCautions.jsx — LA VEILLE DES CAUTIONS, à l'écran.
//
// RENDU HORS DE L'INTERRUPTEUR DE L'OPÉRATEUR, délibérément. Ce bloc ne montre
// pas ce que l'opérateur PROPOSE — il montre ce qui est VRAI dans la base, sur
// de l'argent de voyageur. Le cacher derrière un interrupteur éteint reviendrait
// à rendre invisibles des anomalies qui existent déjà.
//
// IL N'AGIT PAS. Aucun bouton « libérer », « rembourser », « prévenir ». La
// caution est du chemin argent : la veille regarde, Dimitri décide, dans
// l'écran de la réservation où vivent les gardes existantes. Un bouton d'action
// posé ici aurait l'air d'un raccourci et serait un contournement.

import { useState, useEffect, useCallback } from "react";
import { fetchVeilleCautions } from "../../lib/veille-api";

const eur = (n) =>
  n == null ? "" : `${Number(n).toLocaleString("fr-FR", { minimumFractionDigits: 0 })} €`;

const TITRES = {
  immobilisee: "Argent encore retenu",
  remboursement_en_retard: "Remboursement en retard",
  rendue_sans_prevenir: "Rendue sans le dire au voyageur",
  prelevee_sans_motif: "Prélevée sans motif écrit",
  montants_absents: "Répartition introuvable",
};

export default function VeilleCautions() {
  const [etat, setEtat] = useState("chargement");
  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [deplie, setDeplie] = useState(false);

  const charger = useCallback(async () => {
    setEtat("chargement");
    setErreur(null);
    try {
      setDonnees(await fetchVeilleCautions());
      setEtat("ok");
    } catch (e) {
      console.error("[VeilleCautions]", e);
      setErreur(e.message || "lecture impossible");
      setEtat("erreur");
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  if (etat === "chargement") return null; // pas de squelette pour un bloc secondaire

  if (etat === "erreur") {
    return (
      <div data-testid="veille-cautions-erreur"
        style={{ margin: "0 0 12px", padding: "10px 12px", borderRadius: 10,
          background: "#fdecec", color: "#a12b2b", fontSize: 12 }}>
        Les cautions n'ont pas pu être vérifiées ({erreur}). Ce n'est pas « rien à
        signaler » — on n'a pas pu regarder.{" "}
        <button onClick={charger}
          style={{ background: "transparent", border: "none", color: "#a12b2b", fontWeight: 600, textDecoration: "underline", cursor: "pointer", padding: 0 }}>
          Réessayer
        </button>
      </div>
    );
  }

  const alertes = donnees?.alertes || [];
  const indeterminees = donnees?.indeterminees || [];
  const incomplet = donnees?.complet === false;
  const essais = donnees?.mise_au_point || [];

  if (!alertes.length && !indeterminees.length && !incomplet) {
    return (
      <div data-testid="veille-cautions-ok"
        style={{ margin: "0 0 12px", padding: "9px 12px", borderRadius: 10,
          background: "#eef3f8", color: "var(--gris-d)", fontSize: 11.5 }}>
        Cautions : rien à signaler sur les {donnees?.examinees ?? 0} examinées.
        {essais.length > 0 && (
          // Dit à voix haute que la base contient des essais : sans cette
          // ligne, quelqu'un comptant les empreintes à la main trouverait 8
          // là où l'écran annonce 0, et croirait l'écran cassé.
          <>
            {" "}
            <span data-testid="veille-cautions-essais">
              Dont <strong>{essais.length}</strong> empreinte{essais.length > 1 ? "s" : ""} de
              mise au point (avant le {donnees.mise_en_service_reelle}), rangée
              {essais.length > 1 ? "s" : ""} à part.
            </span>
          </>
        )}
      </div>
    );
  }

  const hautes = alertes.filter((a) => a.gravite === "haute").length;

  return (
    <div data-testid="veille-cautions"
      style={{ margin: "0 0 12px", border: "1px solid #e6cfa0", borderRadius: 10,
        background: "#fdf9f1", overflow: "hidden" }}>
      <button onClick={() => setDeplie((v) => !v)}
        style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left",
          padding: "10px 12px", background: "transparent", border: "none", cursor: "pointer",
          fontFamily: "inherit", fontSize: 12.5, color: "#8a5b16", fontWeight: 600 }}>
        <span aria-hidden="true">{deplie ? "▾" : "▸"}</span>
        Cautions — {alertes.length} point{alertes.length > 1 ? "s" : ""} à regarder
        {hautes > 0 && (
          <span style={{ background: "#a12b2b", color: "#fff", borderRadius: 6, padding: "1px 6px", fontSize: 10 }}>
            {hautes} urgent{hautes > 1 ? "s" : ""}
          </span>
        )}
      </button>

      {deplie && (
        <div style={{ padding: "0 12px 12px" }}>
          {incomplet && (
            <div data-testid="veille-cautions-incomplet"
              style={{ marginBottom: 8, padding: "8px 10px", borderRadius: 8, background: "#fdecec", color: "#a12b2b", fontSize: 11.5 }}>
              Vue INCOMPLÈTE — table(s) illisible(s) : {(donnees.sources_illisibles || []).join(", ")}.
              Le compte ci-dessous est un minimum, pas un total.
            </div>
          )}

          {alertes.map((a, i) => (
            <div key={`${a.id}-${a.type}-${i}`} data-testid={`veille-alerte-${a.type}`}
              style={{ padding: "7px 0", borderTop: i ? "1px solid #f0e6d2" : "none", fontSize: 12 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                <span style={{ fontWeight: 600, color: a.gravite === "haute" ? "#a12b2b" : "#8a5b16" }}>
                  {TITRES[a.type] || a.type}
                </span>
                {a.montant_eur != null && <span style={{ color: "var(--gris-d)" }}>{eur(a.montant_eur)}</span>}
                {a.logement_id && <span style={{ color: "var(--gris)", fontSize: 11 }}>{a.logement_id}</span>}
              </div>
              <div style={{ color: "var(--gris-d)", marginTop: 2 }}>{a.phrase}</div>
            </div>
          ))}

          {indeterminees.length > 0 && (
            <div data-testid="veille-cautions-indeterminees"
              style={{ marginTop: 10, paddingTop: 8, borderTop: "1px solid #f0e6d2", fontSize: 11.5, color: "#8a5b16" }}>
              <strong>{indeterminees.length}</strong> caution{indeterminees.length > 1 ? "s" : ""} sur
              lesquelles je ne peux pas conclure :{" "}
              {indeterminees.map((x) => x.motif).join(" · ")}.
            </div>
          )}

          <div style={{ marginTop: 10, fontSize: 11, color: "var(--gris)" }}>
            Ces lignes sont observées, jamais corrigées ici : libérer, rembourser ou
            prévenir le voyageur se fait dans la réservation.
          </div>
        </div>
      )}
    </div>
  );
}
